import { auth } from '../firebase/config.ts';
import { getLocalApiBase } from './scraperOrchestration.ts';

const CLOUD_API_BASE = 'https://scraping-machine-469675949347.europe-west1.run.app';

async function resolveApiBase(): Promise<string> {
  const isLocal =
    typeof window !== 'undefined' &&
    (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
  if (isLocal) {
    const local = await getLocalApiBase();
    if (local) return local;
  }
  return CLOUD_API_BASE;
}

async function getBearerToken(): Promise<string> {
  const user = auth.currentUser;
  if (!user) throw new Error('You must be signed in to run host profile actions.');
  return user.getIdToken();
}

async function postHostProfile<T>(path: string, body: Record<string, unknown>): Promise<T> {
  const base = await resolveApiBase();
  const token = await getBearerToken();
  const url = `${base.replace(/\/$/, '')}${path}`;
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  });
  const text = await res.text();
  let data: any;
  try {
    data = text ? JSON.parse(text) : {};
  } catch {
    data = { raw: text };
  }
  if (!res.ok) {
    const msg = data?.detail || data?.error || text || res.statusText || `HTTP ${res.status}`;
    throw new Error(typeof msg === 'string' ? msg : JSON.stringify(msg));
  }
  return data as T;
}

export const hostProfileApi = {
  discover(body: { sample_size?: number; limit?: number } = {}) {
    return postHostProfile<{
      sampled_posts: number;
      skipped_with_hostID: number;
      candidates: Array<{
        place: string;
        count: number;
        source: string;
        event_url: string;
        samples: unknown[];
        canonical_title?: string;
      }>;
    }>('/api/host-profile/discover', body);
  },

  discoverExact(body: { sample_size?: number; limit?: number } = {}) {
    return postHostProfile<{
      sampled_posts: number;
      matches: Array<{
        host_id: string;
        host_title: string;
        count: number;
        matched_places: [string, number][];
        samples: unknown[];
      }>;
    }>('/api/host-profile/discover-exact', body);
  },

  preview(body: {
    host_id?: string;
    place?: string;
    source?: string;
    event_url?: string;
    website_url?: string;
    sample_events?: number;
    no_repair?: boolean;
    max_repair_attempts?: number;
  }) {
    return postHostProfile<Record<string, unknown>>('/api/host-profile/preview', body);
  },

  validatePreview(preview: Record<string, unknown>) {
    return postHostProfile<Record<string, unknown>>('/api/host-profile/validate-preview', { preview });
  },

  upsert(body: {
    preview: Record<string, unknown>;
    host_id?: string;
    dry_run?: boolean;
    allow_weak_preview?: boolean;
  }) {
    return postHostProfile<Record<string, unknown>>('/api/host-profile/upsert', body);
  },

  applyPreview(body: {
    preview: Record<string, unknown>;
    host_id?: string;
    dry_run?: boolean;
    overwrite_existing?: boolean;
    link_limit?: number | null;
    all_events?: boolean;
    allow_weak_preview?: boolean;
  }) {
    return postHostProfile<Record<string, unknown>>('/api/host-profile/apply-preview', body);
  },

  linkOverrides(body: {
    host_id: string;
    dry_run?: boolean;
    all_events?: boolean;
    overwrite_existing?: boolean;
    limit?: number | null;
  }) {
    return postHostProfile<Record<string, unknown>>('/api/host-profile/link-overrides', body);
  },
};
